import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import Grid from 'apollo-react/components/Grid';
import IconButton from 'apollo-react/components/IconButton';
import IconMenuButton from 'apollo-react/components/IconMenuButton';
import Loader from 'apollo-react/components/Loader';
import MenuButton from 'apollo-react/components/MenuButton';
import Tooltip from 'apollo-react/components/Tooltip';
import Typography from 'apollo-react/components/Typography';
import Info from 'apollo-react-icons/Info';
import Menu from 'apollo-react-icons/Menu';
import classNames from 'classnames';
import qs from 'query-string';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import CreateRepositoryModal from './CreateRepositoryModal';
import SaveAsModal from './SaveAsModal';
import {
  deleteRepositoryConfig,
  publishRepositoryConfig,
  saveRepositoryConfig,
  setDistributionModalOpen,
  unpublishRepositoryConfig,
} from '../../actions/RepositoryConfigurationActions';
import AuthBlueBanner from '../../components/AuthBlueBanner';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal';
import { HOME, REPOSITORY_CONFIGURATION } from '../../constants/Urls';
import { useTranslation } from '../../custom-hooks/useTranslation';
import {
  getRepositoryProp,
  hasRepositoryUnsavedChanges,
} from '../../selectors/RepositoryConfigurationSelectors';
import { getUITA } from '../../utils/commonUtils';

const useStyles = makeStyles({
  wrapper: {
    borderBottom: '1px solid #e9e9e9',
    padding: '20px 0px',
    '& ul': {
      marginLeft: '15px',
    },
    marginLeft: 15,
  },
  infoTooltip: {
    display: 'none',
    '@media (max-width: 1200px)': {
      display: 'block',
    },
  },
  titleWrapper: {
    paddingLeft: 5,
    '@media (max-width: 1200px)': {
      display: 'flex',
      alignItems: 'center',
    },
  },
  buttonsWrapper: {
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
    '@media (max-width: 1200px)': {
      paddingRight: 28,
    },
  },
  hiddenMenuButton: {
    display: 'none',
    '@media (max-width: 1200px)': {
      display: 'block',
    },
  },
  marginRight: {
    marginRight: 15,
    '@media (max-width: 1200px)': {
      display: 'none',
    },
  },
  resetOverflow: {
    overflow: 'visible!important',
    minWidth: 'initial!important',
  },
  repositoryInfo: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'start',
    '@media (max-width: 1200px)': {
      display: 'none',
    },
  },
  infoTextTitle: {
    fontFamily: 'Proxima Nova',
    fontSize: 14,
    fontStyle: 'normal',
    fontStretch: 'normal',
    letterSpacing: 'normal',
    color: 'var(--black)',
  },
  infoTextContainer: {
    marginRight: 55,
    margin: 5,
    display: 'flex',
    alignItems: 'start',
  },
});

const Header = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const translateString = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteModalOpened, setIsDeleteModalOpened] = useState(false);
  const [isSaveAsModalOpen, setIsSaveAsModalOpen] = useState(false);
  const [isCreateRepositoryModalOpen, setIsCreateRepositoryModalOpen] = useState(false);
  const [isSignOffModalOpen, setIsSignOffModalOpen] = useState(false);

  const hasUnsavedChanges = useSelector(hasRepositoryUnsavedChanges);
  const isTemplate = useSelector((state) => getRepositoryProp(state, { propName: 'isTemplate' }));
  const version = useSelector((state) => getRepositoryProp(state, { propName: 'version' }));
  const status = useSelector((state) => getRepositoryProp(state, { propName: 'status' }));
  const repositoryName = useSelector((state) => getRepositoryProp(state, { propName: 'name' }));

  const { isNew } = qs.parse(location.search) || {};

  const redirectToPrevPage = () => {
    if (history.length > 1) {
      history.goBack();
    } else {
      history.push(`${HOME}`);
    }
  };

  const redirectToRepository = ({ repositoryId }) => {
    history.replace(`${REPOSITORY_CONFIGURATION}?repositoryId=${repositoryId}`);
  };

  const handleCancel = () => {
    redirectToPrevPage();
  };

  const handleSave = () => {
    dispatch(saveRepositoryConfig({ setIsLoading, isNew, redirectToRepository }));
  };

  const handleDeleteRepository = async () => {
    setIsDeleteModalOpened(false);
    setIsLoading(true);
    await dispatch(deleteRepositoryConfig({ redirectToPrevPage }));
    setIsLoading(false);
  };

  const handleAuthSuccess = (password) => {
    setIsSignOffModalOpen(false);
    dispatch(
      status === 'Published'
        ? unpublishRepositoryConfig({ redirectToPrevPage, password, setIsLoading })
        : publishRepositoryConfig({ redirectToPrevPage, password, setIsLoading })
    );
  };

  const actionItems = [
    {
      text: translateString('Delete'),
      onClick: () => setIsDeleteModalOpened(true),
      hidden: isNew || status === 'Published',
    },
    {
      text: translateString('Publish'),
      hidden: status === 'Published' || isNew || isTemplate,
      onClick: () => setIsSignOffModalOpen(true),
    },
    {
      text: translateString('Unpublish'),
      hidden: status !== 'Published' || isNew | isTemplate,
      onClick: () => setIsSignOffModalOpen(true),
    },
    {
      text: translateString('Save As'),
      hidden: isNew,
      onClick: () => setIsSaveAsModalOpen(true),
    },
    {
      text: translateString('Create Repository'),
      hidden: !isTemplate,
      onClick: () => setIsCreateRepositoryModalOpen(true),
    },
  ];

  const sortedActionItems = actionItems.sort((a, b) => a.text.localeCompare(b.text));

  const mobileMenuItems = [
    ...sortedActionItems,
    {
      text: 'Distribution List',
      hidden: isNew,
      onClick: () => dispatch(setDistributionModalOpen(true)),
    },
    {
      text: translateString('Cancel'),
      onClick: handleCancel,
    },
    {
      text: translateString('Save'),
      onClick: handleSave,
      disabled: !hasUnsavedChanges,
    },
  ];

  const repoInfo = [
    {
      title: `${translateString('Status')}: ${status}`,
      subtitle: '',
    },
    {
      title: `${translateString('Version')}: ${version}`,
      subtitle: '',
    },
  ];

  const deleteConfirmMessage = (
    <>
      {translateString('Do you really want to delete')} {'Document Repository '}
      <b>{repositoryName}</b>
      {'?'}
    </>
  );

  return (
    <div className={classes.wrapper}>
      <Grid container justifyContent="flex-end" alignItems="center">
        <Grid className={classes.titleWrapper} item xs={8}>
          {!isTemplate && (
            <>
              <span className={classes.infoTooltip}>
                {(version || status) && (
                  <Tooltip extraLabels={repoInfo} placement="right" variant="light">
                    <IconButton aria-label="Info Icon">
                      <Info color="primary" />
                    </IconButton>
                  </Tooltip>
                )}
              </span>
              <div className={classes.repositoryInfo}>
                <div className={classes.infoTextContainer}>
                  {status && (
                    <Typography className={classes.infoTextTitle} style={{ marginRight: 30 }}>
                      {`${translateString('Status')}: ${status}`}
                    </Typography>
                  )}
                  {version && (
                    <Typography className={classes.infoTextTitle}>
                      {`${translateString('Version')}: ${version}`}
                    </Typography>
                  )}
                </div>
              </div>
            </>
          )}
        </Grid>
        <Grid className={classes.buttonsWrapper} item xs={4}>
          <IconMenuButton
            id="menu"
            className={classes.hiddenMenuButton}
            menuItems={mobileMenuItems.filter((action) => !action.hidden)}
          >
            <Menu />
          </IconMenuButton>
          {!isNew && (
            <MenuButton
              buttonText={translateString('Actions')}
              menuItems={sortedActionItems.filter((action) => !action.hidden)}
              className={classNames(classes.marginRight, classes.resetOverflow)}
            />
          )}
          {!isNew && (
            <Button
              variant="primary"
              className={`${classes.marginRight} ${classes.resetOverflow}`}
              onClick={() => dispatch(setDistributionModalOpen(true))}
            >
              {translateString('Distribution List')}
            </Button>
          )}
          <Button
            variant="text"
            className={classNames(classes.marginRight, classes.resetOverflow)}
            onClick={handleCancel}
            ui-ta={getUITA('Editor Header Header Button', 'Cancel')}
          >
            {translateString('Cancel')}
          </Button>
          <Button
            variant="secondary"
            className={classNames(classes.marginRight, classes.resetOverflow)}
            onClick={handleSave}
            ui-ta={getUITA('Entity Editor Header Button', 'Save')}
            disabled={!hasUnsavedChanges}
          >
            {translateString('Save')}
          </Button>
        </Grid>
        <DeleteConfirmationModal
          message={deleteConfirmMessage}
          open={isDeleteModalOpened}
          onClose={() => setIsDeleteModalOpened(false)}
          onDeleteConfirm={handleDeleteRepository}
        />
        {isSaveAsModalOpen && (
          <SaveAsModal
            initialRepositoryName={repositoryName}
            isOpen={isSaveAsModalOpen}
            handleClose={() => setIsSaveAsModalOpen(false)}
            setIsLoading={setIsLoading}
            redirectToRepository={redirectToRepository}
          />
        )}
        {isCreateRepositoryModalOpen && (
          <CreateRepositoryModal
            isOpen={isCreateRepositoryModalOpen}
            initialRepositoryName={repositoryName}
            handleClose={() => setIsCreateRepositoryModalOpen(false)}
            setIsLoading={setIsLoading}
            redirectToRepository={redirectToRepository}
          />
        )}
        {isSignOffModalOpen && (
          <AuthBlueBanner
            open={isSignOffModalOpen}
            handleDialogClose={() => setIsSignOffModalOpen(false)}
            onAuthSuccess={handleAuthSuccess}
            setAuthenticating={setIsLoading}
            submitButtonLabel="Sign-Off"
          />
        )}
      </Grid>
      {isLoading && <Loader isInner />}
    </div>
  );
};

export default Header;
